import {
  courtIdentityKey,
  type CaseInfoState,
  type EFilingParty,
  type EFilingAttorney,
  type EFilingDocument,
} from '@lds-efsp/shared-contracts';

/** The filing court, as the two court-change handlers hold it. */
export interface FilingCourtSnapshot {
  state?: string;
  county?: string;
}

/**
 * The context writers the reset needs. Passed in (rather than reaching for the
 * context here) so the pure reset logic has a single implementation every call
 * site shares — OrderInfoTab (Where to File), InlineCourtSelector (Upload Court
 * card), and DocumentsTab's "Change court/case" affordance.
 */
export interface ResetCourtScopedCaseCtx {
  /** The canonical "a case is attached" flag — `wizardState.caseInfo.caseTrackingId`. */
  caseTrackingId: string;
  updateCaseInfo: (updates: Partial<CaseInfoState>) => void;
  setParties: (parties: EFilingParty[]) => void;
  setAttorneys: (attorneys: EFilingAttorney[]) => void;
  /**
   * The site-level gate for the SPEC-SOR-174 R17 wipe below. Absent/false
   * leaves the classic flow byte-for-byte as it shipped.
   */
  confirmFlowEnabled?: boolean;
  /** Current documents — required for the R17 document reset. */
  documents?: EFilingDocument[];
  setDocuments?: (documents: EFilingDocument[]) => void;
  /** Returns every guided section to `pending` and discards the filer's acts. */
  resetSectionStatuses?: () => void;
  /**
   * The filing court BEFORE the write that triggered this reset, and the court
   * being written. R17's antecedent is "WHEN the FilingCourt CHANGES", and the
   * confirm-flow leg below is destructive enough that the antecedent has to be
   * CHECKED rather than assumed from the fact that a handler ran: it discards
   * the category and type, the whole roster, and every document's type and
   * metadata. A same-value write — a re-selection of the county already set, a
   * handler that fires twice, a synthetic change event, a remount — must be a
   * no-op, not a wipe.
   *
   * Both are REQUIRED (not optional-with-a-default) on purpose. A default would
   * have to pick a side: "assume changed" re-opens exactly this defect for the
   * next call site that forgets, "assume unchanged" silently disables R17 for
   * it. Making the transition part of the call forces every site to state it,
   * which is the same declared-not-inferred discipline the binding provenance
   * follows. The classic call site (DocumentsTab's case detach) states the
   * court is unchanged, which is true — it changes the CASE.
   */
  previousCourt: FilingCourtSnapshot;
  nextCourt: FilingCourtSnapshot;
}

/**
 * R17's antecedent. Compared through `courtIdentityKey` — the one canonical
 * court comparator — rather than `!==`, because the same court legitimately
 * arrives in two spellings: OCR writes a slug ('madera') while the county
 * selects carry the courts API display name ('Madera'), and the R85
 * canonicalization effect rewrites one into the other AFTER the counties load.
 * A raw comparison would read that rewrite as a court change and wipe the
 * filing out from under a filer who touched nothing.
 */
function filingCourtChanged(previous: FilingCourtSnapshot, next: FilingCourtSnapshot): boolean {
  return (
    courtIdentityKey(previous.state ?? '') !== courtIdentityKey(next.state ?? '') ||
    courtIdentityKey(previous.county ?? '') !== courtIdentityKey(next.county ?? '')
  );
}

/**
 * SOR-173/SOR-194: the OCR category/type-recovery set. Every member is derived
 * from ONE court's policy — a rejected extracted name is "rejected" only
 * relative to that court's category or type list, and the candidates are that
 * court's codes — so all are cleared wherever the filing court changes. A quote
 * left standing under the new court would explain a rejection that court never
 * made, beside candidates it does not publish.
 */
const CLEAR_CATEGORY_RECOVERY = {
  caseCategoryCandidates: undefined,
  ocrExtractedCategoryName: undefined,
  ocrExtractedCaseTypeName: undefined,
} satisfies Partial<CaseInfoState>;

/**
 * Reset the court-scoped case selection when the filing court (state/county)
 * changes or the filer explicitly detaches the case.
 *
 * SOR-154 shipped this in OrderInfoTab; SOR-162 factors it out verbatim so the
 * Where-to-File county change, the Upload-step Court-card county change
 * (InlineCourtSelector), and the Upload-step "Change court/case" affordance all
 * clear the SAME state through one code path — divergence here is a misfiling
 * risk, which is the whole point of the SOR-154 wipe.
 *
 * When a case is attached (`caseTrackingId` truthy) the whole case selection and
 * its dependent roster are cleared: the case belongs to the old court and can be
 * neither carried into nor submitted under the new one. When no case is attached
 * only `courtJurisdiction` is cleared (WOL-341) — a case-initiation filing's
 * manually-added parties are preserved.
 *
 * SPEC-SOR-174 R17 generalizes that in the CONFIRM FLOW ONLY: the court change
 * wipes everything below the court whatever its source, so no approval granted
 * for the previous court survives into the new one. That deliberately REVERSES
 * the WOL-341 manual-party preservation — "if I go back up to change the filing
 * county everything should be wiped blank." The classic flow keeps the shipped
 * behaviour exactly, and this stays the single chokepoint (SOR-162): every
 * court-change site already calls it inside the same handler as the county
 * write, so R17 extends it rather than adding a second reset path.
 */
export function resetCourtScopedCaseData(ctx: ResetCourtScopedCaseCtx): void {
  const { caseTrackingId, updateCaseInfo, setParties, setAttorneys } = ctx;
  if (caseTrackingId) {
    updateCaseInfo({
      caseTitle: '',
      caseCategoryCode: '',
      caseCategoryName: '',
      caseType: null,
      caseSubType: null,
      trialCourtCaseNumber: '',
      caseTrackingId: '',
      complaintId: '',
      complaintTitle: '',
      subcaseCount: 0,
      subcases: [],
      caseTypeMetadata: null,
      courtJurisdiction: null,
      // SOR-191: the binding and its provenance clear together — the pair is one
      // record, and a `case-record` tag surviving on a cleared value would claim
      // the record asserted an empty court.
      caseSourceCounty: '',
      caseSourceCountyProvenance: undefined,
      ...CLEAR_CATEGORY_RECOVERY,
    });
    setParties([]);
    setAttorneys([]);
  } else {
    // No case attached, so the case selection itself is preserved (WOL-341) —
    // but the category-recovery state is cleared here too. Unlike the case
    // fields, it is not merely stale under the new court, it is WRONG there:
    // the candidate codes come from the previous court's policy, and the
    // Upload-step chips offer them as one click each. Nothing filters them
    // against the new court's list (SOR-173 removed that filter so the
    // resolution's candidates are never silently dropped), so a survivor would
    // write a foreign category code into the filing.
    updateCaseInfo({ courtJurisdiction: null, ...CLEAR_CATEGORY_RECOVERY });
  }

  if (!ctx.confirmFlowEnabled) return;
  // R17 fires on a court CHANGE, not on a court write. See `previousCourt`.
  if (!filingCourtChanged(ctx.previousCourt, ctx.nextCourt)) return;

  // ── SPEC-SOR-174 R17, confirm flow only ──────────────────────────────────
  // Everything below the court is scoped to the court that is going away.

  // The case category and type. R17 requires these cleared with no attach
  // guard, and the branch above only clears them on the ATTACHED leg — so
  // without this a case-initiation filing carried the previous court's
  // category code and type across the change. They are court taxonomy: a
  // Madera category code names nothing under Orange, and every downstream
  // consumer (the doc-type list, the envelope's CaseCourt routing) reads them
  // as if the new court had issued them. Idempotent on the attached leg.
  updateCaseInfo({
    caseCategoryCode: '',
    caseCategoryName: '',
    caseType: null,
    caseSubType: null,
  });

  // The whole roster, whatever its source. The WOL-341 carve-out above spares
  // manually-added parties on an unattached filing; R17 drops that on purpose —
  // a manual roster entered for the previous court is no more portable to the
  // new one than an imported case roster is.
  setParties([]);
  setAttorneys([]);

  // Each document's type and its metadata, while the document and its uploaded
  // file are RETAINED — re-uploading is not the correction for a wrong county.
  // `metadataValues` is cleared wholesale rather than filtered down to the
  // party-class references: those references are the load-bearing part (the
  // roster they point at is now empty, so every one of them is an orphan), but
  // the rest of the map is per-doc-type policy metadata keyed to a documentType
  // that is itself being cleared and to the previous court's policy. Same
  // reasoning as CLEAR_CATEGORY_RECOVERY above — under the new court those
  // values are not merely stale, they are wrong, and nothing filters them.
  if (ctx.documents && ctx.setDocuments) {
    ctx.setDocuments(
      ctx.documents.map(doc => ({
        ...doc,
        documentType: undefined,
        documentTitle: undefined,
        // formGroups is derived from documentType at every assignment site, so
        // it cannot outlive it without breaking lead validation.
        formGroups: undefined,
        metadataValues: {},
        // SPEC-SOR-220-v2: provenance rides BESIDE metadataValues, so the one
        // wipe that empties the values empties it too — a `cleared` slot left
        // behind would survive the court change into a draft with an empty
        // roster and no referent for the filer to resolve it by. `undefined`
        // is the canonical absent (the map is sparse; absence IS "nothing
        // recorded").
        assignmentProvenance: undefined,
      })),
    );
  }

  // Every section back to pending, every filer confirm act discarded.
  ctx.resetSectionStatuses?.();
}
